import type { TeamMember } from '@appic/data';
import { z } from 'zod';

export type DomainRole = TeamMember['role'];

export interface UserProfile {
	id: string;
	email: string;
	fullName: string | null;
	role: DomainRole;
	plzAreas: string[];
	phone?: string | null;
	isActive: boolean;
	externalDomain?: string | null;
	teamId?: string | null;
	homeAddress?: string | null;
	homeLat?: number | null;
	homeLng?: number | null;
	color?: string | null;
	initials?: string | null;
	isAiAgent?: boolean;
	principalId?: string | null;
	lastSignInAt?: string | null;
}

const UserRow = z.object({
	id: z.union([z.string(), z.number()]),
	email: z.string(),
	full_name: z.string().nullable().optional(),
	role: z.string().optional(),
	plz_areas: z.array(z.any()).nullable().optional(),
	phone: z.string().nullable().optional(),
	is_active: z.boolean().nullable().optional(),
	external_domain: z.string().nullable().optional(),
	team_id: z.string().nullable().optional(),
	home_address: z.string().nullable().optional(),
	home_lat: z.number().nullable().optional(),
	home_lng: z.number().nullable().optional(),
	color: z.string().nullable().optional(),
	initials: z.string().nullable().optional(),
	is_ai_agent: z.boolean().nullable().optional(),
	principal_id: z.string().nullable().optional(),
	last_sign_in_at: z.string().nullable().optional(),
});

function mapRowToUser(row: unknown): UserProfile {
	const r = UserRow.safeParse(row);
	if (!r.success) throw new Error('Invalid user row');
	const x = r.data;
	return {
		id: String(x.id),
		email: String(x.email),
		fullName: x.full_name ?? null,
		role: (x.role as DomainRole) || 'field_sales',
		plzAreas: Array.isArray(x.plz_areas) ? x.plz_areas.map(String) : [],
		phone: x.phone ?? null,
		isActive: Boolean(x.is_active ?? true),
		externalDomain: x.external_domain ?? null,
		teamId: x.team_id ?? null,
		homeAddress: x.home_address ?? null,
		homeLat: x.home_lat ?? null,
		homeLng: x.home_lng ?? null,
		color: x.color ?? null,
		initials: x.initials ?? null,
		isAiAgent: Boolean(x.is_ai_agent ?? false),
		principalId: x.principal_id ?? null,
		lastSignInAt: x.last_sign_in_at ?? null,
	};
}

function toSnakeCase(profile: Partial<UserProfile>): Record<string, unknown> {
	const payload: Record<string, unknown> = {};
	if (profile.email !== undefined) payload.email = profile.email;
	if (profile.fullName !== undefined) payload.full_name = profile.fullName;
	if (profile.role !== undefined) payload.role = profile.role;
	if (profile.plzAreas !== undefined) payload.plz_areas = profile.plzAreas;
	if (profile.phone !== undefined) payload.phone = profile.phone;
	if (profile.isActive !== undefined) payload.is_active = profile.isActive;
	if (profile.externalDomain !== undefined) payload.external_domain = profile.externalDomain;
	if (profile.teamId !== undefined) payload.team_id = profile.teamId;
	if (profile.homeAddress !== undefined) payload.home_address = profile.homeAddress;
	if (profile.homeLat !== undefined) payload.home_lat = profile.homeLat;
	if (profile.homeLng !== undefined) payload.home_lng = profile.homeLng;
	if (profile.color !== undefined) payload.color = profile.color;
	if (profile.initials !== undefined) payload.initials = profile.initials;
	if (profile.isAiAgent !== undefined) payload.is_ai_agent = profile.isAiAgent;
	if (profile.principalId !== undefined) payload.principal_id = profile.principalId;
	return payload;
}

async function sf() {
	const { serviceFetch } = await import('../http/serviceLayerClient');
	return serviceFetch;
}

export async function getUserByEmail(email: string): Promise<UserProfile | null> {
	const users = await listUsers();
	return users.find(u => u.email.toLowerCase() === email.toLowerCase()) ?? null;
}

export async function listUsers(): Promise<UserProfile[]> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/users', { method: 'GET' });
	if (response.status !== 200) {
		// eslint-disable-next-line no-console
		console.warn('[users.listUsers] non-200', { status: response.status });
		throw new Error(`Failed to fetch users: ${response.status}`);
	}
	const result = response.data as any;
	// Diagnostic per MO-FE-OPINION-REMOVAL-VENDOR-HOOKS-V1 §1.6: pin which of
	// F1-F5 produces the empty impersonation dropdown. Captures `success`
	// flag + row count + envelope keys so staging logs reveal envelope drift
	// / RLS-empty / endpoint-missing without needing a network inspector.
	// Remove once the vendor-hook cutover in B7 lands.
	// eslint-disable-next-line no-console
	console.warn('[users.listUsers] response shape', {
		hasSuccess: typeof result?.success !== 'undefined',
		successValue: result?.success,
		hasData: typeof result?.data !== 'undefined',
		dataIsArray: Array.isArray(result?.data),
		rowCount: Array.isArray(result?.data) ? result.data.length : null,
		envelopeKeys: result && typeof result === 'object' ? Object.keys(result) : null,
	});
	if (result.success && result.data) {
		return result.data.map(mapRowToUser);
	}
	throw new Error('Unexpected response from /v1/users');
}

export async function createUser(profile: Omit<UserProfile, 'id'> & { id?: string }): Promise<UserProfile> {
	const serviceFetch = await sf();
	const response = await serviceFetch('/v1/users', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(toSnakeCase(profile)),
	});
	if (response.status !== 200 && response.status !== 201) {
		throw new Error(`Failed to create user: ${response.status}`);
	}
	const result = response.data as any;
	if (result.success && result.data) {
		return mapRowToUser(result.data);
	}
	throw new Error('Unexpected response from POST /v1/users');
}

export async function updateUser(id: string, updates: Partial<UserProfile>): Promise<UserProfile> {
	const serviceFetch = await sf();
	const response = await serviceFetch(`/v1/users/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(toSnakeCase(updates)),
	});
	if (response.status !== 200) {
		throw new Error(`Failed to update user: ${response.status}`);
	}
	const result = response.data as any;
	if (result.success && result.data) {
		return mapRowToUser(result.data);
	}
	throw new Error('Unexpected response from PATCH /v1/users');
}

export async function deleteUser(id: string): Promise<void> {
	const serviceFetch = await sf();
	const response = await serviceFetch(`/v1/users/${id}`, { method: 'DELETE' });
	if (response.status !== 200 && response.status !== 204) {
		throw new Error(`Failed to delete user: ${response.status}`);
	}
}

/**
 * Update the PLZ areas for a user (canonical read surface for the RI
 * mission generator).
 *
 * Service Layer endpoint added in PR #1267 — body is `{plz_areas: string[]}`
 * (5-digit `^\d{5}$`, max 500, dedup'd server-side). Auth: manager /
 * sales_manager / superadmin OR self.
 *
 * PUT /v1/users/{email}/plz-areas
 */
export async function updateUserPlzAreas(email: string, plzAreas: string[]): Promise<UserProfile> {
	const serviceFetch = await sf();
	const response = await serviceFetch(`/v1/users/${encodeURIComponent(email)}/plz-areas`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ plz_areas: plzAreas }),
	});
	if (response.status !== 200) {
		throw new Error(`Failed to update user PLZ areas: ${response.status}`);
	}
	const result = response.data as any;
	if (result.success && result.data) {
		return mapRowToUser(result.data);
	}
	throw new Error('Unexpected response from PUT /v1/users/{email}/plz-areas');
}

export function mapDomainRoleToUiRole(role: DomainRole): 'Admin' | 'Sales' | 'Viewer' | 'Manager' {
	if (role === 'superadmin') return 'Admin';
	if (role === 'sales_manager') return 'Manager';
	if (role === 'field_sales' || role === 'handelsvertreter' || role === 'caller') return 'Sales';
	if (role === 'ai_agent') return 'Viewer';
	return 'Viewer';
}

/**
 * Fetch the public.users row for the JWT subject's email. Returns a synthetic
 * minimal profile when the row is absent (RLS-scoped listUsers may exclude the
 * caller's own row, OR provisioning hasn't run yet) — caller-side fallback so
 * UI never blocks on the lookup.
 *
 * IMPORTANT (forensic 2026-05-04 SL audit, ref: SL findings on POST /v1/users
 * 500-spam): FE MUST NOT POST /v1/users from this bootstrap. Provisioning of
 * public.users rows is server-owned (Supabase trigger / SL admin endpoint),
 * NOT a frontend responsibility. The previous implementation fell back to
 * createUser() on miss and spammed 500s every time RLS-scoped listUsers
 * omitted the caller's own row — see FE Sys Prompt §Architectural Posture
 * ("No FE business logic. FE renders what backend says."). This function
 * now ONLY READS; if the row is genuinely missing, escalate via SL, not FE.
 *
 * Result is cached in-process per-email so navigation between pages doesn't
 * re-fire listUsers() on each UserContext effect re-run. Module-level Map
 * (NOT sessionStorage — storageFence forbids client storage outside the PWA
 * boundary; cache resets on full page reload, which is the correct stale-data
 * boundary for this lookup).
 */
const profileMemoryCache = new Map<string, UserProfile>();

function syntheticProfile(email: string, fullName?: string | null): UserProfile {
	return {
		id: '',
		email,
		fullName: fullName ?? null,
		role: 'field_sales',
		plzAreas: [],
		phone: null,
		isActive: true,
		externalDomain: null,
		teamId: null,
		homeAddress: null,
		homeLat: null,
		homeLng: null,
		color: null,
		initials: null,
		isAiAgent: false,
		principalId: null,
		lastSignInAt: null,
	};
}

export async function ensureUserProfileFromLogin(
	email: string,
	fullName?: string | null,
	authUserId?: string | null,
): Promise<UserProfile> {
	// The profile id IS the authenticated user's id (auth.uid). Supabase
	// provisions public.users.id = auth.users.id, so the uid is the canonical
	// primary key — SL-confirmed 2026-06-15 (BUG-SL-RUEDIGER-RLS-USERS-WRONG-ROW
	// -DUP-ACCOUNTS). Resolve by uid PRIMARY, email SECONDARY: when a rep's login
	// email drifts from their users.email (Rüdiger: email changed to
	// externclanda.de, the caller row kept the old clanda.de), the email match
	// lands on a sync-created field_sales DUPLICATE — which has no auth.users
	// entry and so can never be the real caller — giving a wrong/empty caller_id
	// and a dark cockpit. Overriding the resolved id with the uid makes
	// claim/session/submit carry the correct caller_id. Under impersonation the
	// caller passes NO authUserId, so the impersonated identity still resolves by
	// email (the admin's uid must never leak onto the impersonated profile).
	const uid = typeof authUserId === 'string' && authUserId ? authUserId : null;
	const cacheKey = `${email.toLowerCase()}|${uid ?? ''}`;
	const hit = profileMemoryCache.get(cacheKey);
	if (hit) return hit;

	const users = await listUsers();
	const byUid = uid ? users.find(u => u.id === uid) ?? null : null;
	const byEmail = users.find(u => u.email.toLowerCase() === email.toLowerCase()) ?? null;
	const base = byUid ?? byEmail ?? syntheticProfile(email, fullName);
	const resolved: UserProfile = uid ? { ...base, id: uid } : base;
	profileMemoryCache.set(cacheKey, resolved);
	return resolved;
}
